import React, { useState } from 'react';
import { Search, Plus, ArrowLeft, Eye, Loader2, CheckCircle2, AlertCircle, Globe, FileSpreadsheet, Link2, Database, Edit, Trash2, Clock, RefreshCw, X } from 'lucide-react';
import DatasourceForm from '../components/DatasourceForm';
import { SimpleApiForm } from '../components/ApiRest/SimpleApiForm';
import { DataSourceCard } from '../components/DataSourceCard';
import ConfirmDialog from '../../../shared/ui/ConfirmDialog';
import { ViewDataModal } from '../components/ViewDataModal';
import ProtectedView from '../../auth/components/ProtectedView';
import { isAdmin, canManageDataSources, canViewDataSources } from '../../../utils/permissions';
import api from '../../../shared/services/apiClient';
import toast from 'react-hot-toast';
import type { DataSource } from '@/types';

type Props = {
  mockDataSources: DataSource[];
  currentUser?: any;
  addDataSource?: (d: Partial<DataSource>) => void;
  updateDataSource?: (id: string | number, d: Partial<DataSource>) => void;
  deleteDataSource?: (id: string | number) => void;
  datasourcesLoading?: boolean;
  datasourcesError?: boolean;
  selectedCompanyId?: string | number;
  companyName?: string;
  companies?: any[];
  setSelectedCompanyId?: (id?: string | number) => void;
  onNavigateToModel?: (datasourceId: string | number) => void;
  onReload?: () => Promise<void>;
};

const DataSourcesView: React.FC<Props> = ({
  mockDataSources,
  datasourcesLoading = false,
  datasourcesError = false,
  currentUser,
  addDataSource,
  updateDataSource,
  deleteDataSource,
  selectedCompanyId,
  companyName,
  companies,
  setSelectedCompanyId,
  onNavigateToModel,
  onReload,
}) => {
  const [currentScreen, setCurrentScreen] = useState<'list' | 'select-type'>('list');
  const [showForm, setShowForm] = useState(false);
  const [showApiRestForm, setShowApiRestForm] = useState(false);
  const [editing, setEditing] = useState<DataSource | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [analyzingId, setAnalyzingId] = useState<string | number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; sourceId: string | number | null }>({ isOpen: false, sourceId: null });
  
  // Modal para ver datos en tiempo real
  const [viewModal, setViewModal] = useState<{ isOpen: boolean; datasource: DataSource | null }>({ 
    isOpen: false, 
    datasource: null 
  });

  const canManage = canManageDataSources(currentUser);

  // Filtrar datasources
  const filteredDataSources = mockDataSources.filter(ds => {
    const matchesSearch = ds.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || 
                       (filterType === 'sheets' && ds.type === 'GOOGLE_SHEETS') ||
                       (filterType === 'api' && ds.type === 'API_REST');
    return matchesSearch && matchesType;
  });

  const handleCreateNew = (type: 'sheets' | 'api') => {
    setEditing(undefined);
    if (type === 'api') {
      setShowApiRestForm(true);
      // NO cambiar currentScreen - los modales se renderizan al final
    } else {
      setShowForm(true);
      // NO cambiar currentScreen - los modales se renderizan al final
    }
  };

  const handleEdit = async (datasource: DataSource) => {
    try {
      const response = await api.get(`/datasources/${datasource.id}`);
      const fullData = response.data;
      
      setEditing(fullData);
      if (fullData.type === 'API_REST') {
        setShowApiRestForm(true);
      } else {
        setShowForm(true);
      }
    } catch (error: any) {
      console.error("Error loading datasource:", error);
      toast.error("Error al cargar la fuente de datos");
    }
  };

  const handleDelete = async (sourceId: string | number) => {
    try {
      if (deleteDataSource) {
        deleteDataSource(sourceId);
      } else {
        await api.delete(`/datasources/${sourceId}`);
      }
    } catch (error: any) {
      console.error("Error deleting datasource:", error);
      
      // Handle 409 Conflict (dependency error) with clear message
      if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'No se puede eliminar esta fuente porque está en uso';
        toast.error(message, { duration: 6000 });
      } else {
        toast.error(error.response?.data?.message || "Error al eliminar la fuente de datos");
      }
    } finally {
      setConfirmDelete({ isOpen: false, sourceId: null });
    }
  };

  const handleAnalyze = async (datasourceId: string | number) => {
    setAnalyzingId(datasourceId);
    try {
      await api.post(`/datasources/${datasourceId}/analyze`);
      toast.success('✅ Análisis completado');
      if (onReload) await onReload();
    } catch (error: any) {
      console.error("Error analyzing datasource:", error);
      toast.error(error.response?.data?.message || "Error al analizar");
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleViewData = (datasource: DataSource) => {
    setViewModal({ isOpen: true, datasource });
  };

  // Header Component
  const Header = ({ showBack = false, onBack }: { showBack?: boolean; onBack?: () => void }) => (
    <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-4">
          {showBack && (
            <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft size={20} />
              Volver
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Render screen content based on state
  let screenContent;

  // Loading State
  if (datasourcesLoading && currentScreen === 'list') {
    screenContent = (
      <ProtectedView
        hasPermission={canViewDataSources(currentUser)}
        message="⚠️ ACCESO RESTRINGIDO: Debes completar el onboarding de tu empresa para acceder a las fuentes de datos."
      >
        <div className="min-h-screen bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-slate-700/80 bg-slate-800/60 p-12 text-slate-200">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
              <p className="text-sm sm:text-base font-medium">Cargando fuentes de datos...</p>
            </div>
          </div>
        </div>
      </ProtectedView>
    );
  }

  // Error State
  else if (datasourcesError && currentScreen === 'list') {
    screenContent = (
      <ProtectedView
        hasPermission={canViewDataSources(currentUser)}
        message="⚠️ ACCESO RESTRINGIDO: Debes completar el onboarding de tu empresa para acceder a las fuentes de datos."
      >
        <div className="min-h-screen bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 py-16">
            <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-12 text-center text-red-200">
              <h2 className="text-lg font-semibold text-red-100">No pudimos cargar las fuentes de datos</h2>
              <p className="mt-2 text-sm text-red-200/80">Reintentá la carga o revisá tu conexión.</p>
              <button
                onClick={() => {
                  if (onReload) {
                    onReload();
                  } else {
                    window.location.reload();
                  }
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-red-400/60 px-4 py-2 text-sm font-medium text-red-100 transition-colors hover:bg-red-500/10"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      </ProtectedView>
    );
  }

  // Empty State Screen
  else if (mockDataSources.length === 0 && currentScreen === 'list') {
    screenContent = (
      <ProtectedView 
        hasPermission={canViewDataSources(currentUser)}
        message="⚠️ ACCESO RESTRINGIDO: Debes completar el onboarding de tu empresa para acceder a las fuentes de datos."
      >
        <div className="min-h-screen bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-white">Mis Fuentes de Datos</h2>
              {canManage && (
                <button 
                  onClick={() => setCurrentScreen('select-type')}
                  className="flex items-center gap-2 bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 font-medium transition-colors"
                >
                  <Plus size={20} />
                  Nueva Fuente
                </button>
              )}
            </div>

            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl shadow-lg border border-slate-700/50 overflow-hidden backdrop-blur-sm">
              <div className="relative px-8 py-16 text-center">
                {/* Decorative background */}
                <div className="absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-cyan-500/5" />
                <div className="absolute top-0 left-0 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl translate-y-1/2 translate-x-1/2" />
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-sky-500/20 to-cyan-500/20 rounded-2xl mb-6 ring-1 ring-sky-500/30">
                    <Database size={40} className="text-sky-400" />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Todavía no tenés fuentes de datos conectadas
                  </h3>
                  
                  {/* Description */}
                  <p className="text-slate-400 text-base max-w-md mx-auto mb-8">
                    Conectá APIs, Google Sheets u otras fuentes para empezar a analizar información en tiempo real
                  </p>

                  {/* Primary CTA */}
                  {canManage && (
                    <button 
                      onClick={() => setCurrentScreen('select-type')}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl"
                    >
                      <Plus size={20} />
                      Conectar Mi Primera Fuente
                    </button>
                  )}

                  {/* Secondary Info */}
                  <div className="mt-8 pt-8 border-t border-slate-700/50">
                    <p className="text-sm text-slate-500 mb-4">¿Qué fuentes podés conectar?</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
                      <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50">
                        <div className="text-sky-400 mb-2">📊</div>
                        <h4 className="text-white font-medium text-sm mb-1">Google Sheets</h4>
                        <p className="text-xs text-slate-500">Conectá hojas de cálculo existentes</p>
                      </div>
                      <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50">
                        <div className="text-cyan-400 mb-2">🔌</div>
                        <h4 className="text-white font-medium text-sm mb-1">APIs REST</h4>
                        <p className="text-xs text-slate-500">Integrá cualquier API externa</p>
                      </div>
                      <div className="p-4 rounded-xl bg-slate-800/40 border border-slate-700/50">
                        <div className="text-sky-400 mb-2">🤖</div>
                        <h4 className="text-white font-medium text-sm mb-1">Análisis Automático</h4>
                        <p className="text-xs text-slate-500">IA detecta estructura y métricas</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ProtectedView>
    );
  }

  // Select Type Screen
  else if (currentScreen === 'select-type') {
    screenContent = (
      <ProtectedView 
        hasPermission={canViewDataSources(currentUser)}
        message="⚠️ ACCESO RESTRINGIDO: Debes completar el onboarding de tu empresa para acceder a las fuentes de datos."
      >
        <div className="min-h-screen bg-slate-900">
          <Header showBack onBack={() => setCurrentScreen('list')} />
          
          <div className="max-w-4xl mx-auto px-6 py-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-3">
                Nueva Fuente de Datos
              </h2>
              <p className="text-lg text-slate-400">
                ¿De dónde vienen tus datos?
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div 
                onClick={() => handleCreateNew('sheets')}
                className="bg-slate-800 rounded-2xl border-2 border-slate-700 hover:border-sky-500 p-8 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-sky-500/20 rounded-xl flex items-center justify-center group-hover:bg-sky-500/30 transition-colors">
                    <FileSpreadsheet className="text-sky-400" size={32} />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white text-center mb-3">
                  Google Sheets
                </h3>
                
                <p className="text-slate-400 text-center mb-6">
                  Conectá hojas de cálculo públicas o privadas
                </p>

                <button className="w-full bg-slate-700 text-slate-300 py-3 rounded-lg font-medium group-hover:bg-sky-600 group-hover:text-white transition-colors">
                  Seleccionar
                </button>
              </div>

              <div 
                onClick={() => handleCreateNew('api')}
                className="bg-slate-800 rounded-2xl border-2 border-slate-700 hover:border-blue-500 p-8 hover:shadow-lg transition-all cursor-pointer group"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-xl flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
                    <Link2 className="text-blue-400" size={32} />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-white text-center mb-3">
                  API REST
                </h3>
                
                <p className="text-slate-400 text-center mb-6">
                  Conectá servicios web y APIs externas
                </p>

                <button className="w-full bg-slate-700 text-slate-300 py-3 rounded-lg font-medium group-hover:bg-blue-600 group-hover:text-white transition-colors">
                  Seleccionar
                </button>
              </div>
            </div>
          </div>
        </div>
      </ProtectedView>
    );
  }

  // Main List View
  else {
    screenContent = (
      <ProtectedView 
        hasPermission={canViewDataSources(currentUser)}
        message="⚠️ ACCESO RESTRINGIDO: Debes completar el onboarding de tu empresa para acceder a las fuentes de datos."
      >
        <div className="min-h-screen bg-slate-900">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header with actions */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white">Mis Fuentes de Datos</h2>
                <p className="text-slate-400 mt-1">Gestiona las fuentes de datos del sistema</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Company selector for INTERNAL users */}
                {currentUser?.userType === 'INTERNAL' && companies && (
                  <select
                    className="bg-slate-800 text-slate-300 px-3 py-2 rounded-lg border border-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    value={selectedCompanyId ?? ''}
                    onChange={e => {
                      const v = e.target.value ? Number(e.target.value) : undefined;
                      setSelectedCompanyId && setSelectedCompanyId(v);
                    }}
                  >
                    <option value="">Todas las empresas</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
                {canManage && (
                  <button 
                    onClick={() => setCurrentScreen('select-type')}
                    className="flex items-center gap-2 bg-sky-600 text-white px-4 py-2 rounded-lg hover:bg-sky-700 font-medium transition-colors"
                  >
                    <Plus size={20} />
                    Nueva Fuente
                  </button>
                )}
              </div>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar fuentes..."
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
              <select 
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="all">Todas</option>
                <option value="sheets">Google Sheets</option>
                <option value="api">API REST</option>
              </select>
            </div>

            {/* Data Sources Grid */}
            {filteredDataSources.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
                <Database className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-2">No se encontraron fuentes de datos</p>
                {searchQuery && (
                  <p className="text-slate-500 text-sm">
                    Intenta con otros términos de búsqueda
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {filteredDataSources.map((datasource) => (
                  <DataSourceCard
                    key={datasource.id}
                    datasource={datasource}
                    onView={handleViewData}
                    onEdit={handleEdit}
                    onDelete={(ds) => setConfirmDelete({ isOpen: true, sourceId: ds.id })}
                    onAnalyze={handleAnalyze}
                    isAnalyzing={analyzingId === datasource.id}
                    canManage={canManage}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </ProtectedView>
    );
  }

  // Render everything with modals at root level
  return (
    <>
      {screenContent}

      {/* Modals - Always rendered at root level, independent of screen */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <DatasourceForm
              datasource={editing as any}
              currentUser={currentUser}
              onClose={() => {
                setShowForm(false);
                setEditing(undefined);
              }}
              onSave={(data) => {
                if (data.id) {
                  updateDataSource && updateDataSource(data.id, data as any);
                } else {
                  addDataSource && addDataSource(data as Partial<DataSource>);
                }
                setShowForm(false);
                setEditing(undefined);
                if (onReload) onReload();
              }}
            />
          </div>
        </div>
      )}

      {showApiRestForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <SimpleApiForm
            onClose={() => {
              setShowApiRestForm(false);
              setEditing(undefined);
              if (onReload) onReload();
            }}
          />
        </div>
      )}
      <ConfirmDialog
        isOpen={confirmDelete.isOpen}
        title="Eliminar fuente de datos"
        message="¿Estás seguro que deseas eliminar esta fuente de datos? Esta acción no se puede deshacer."
        onConfirm={() => confirmDelete.sourceId && handleDelete(confirmDelete.sourceId)}
        onCancel={() => setConfirmDelete({ isOpen: false, sourceId: null })}
      />

      {/* ViewDataModal para ver datos en tiempo real */}
      {viewModal.isOpen && viewModal.datasource && (
        <ViewDataModal
          datasourceId={String(viewModal.datasource.id)}
          datasourceName={viewModal.datasource.name}
          onClose={() => setViewModal({ isOpen: false, datasource: null })}
        />
      )}
    </>
  );
};

export default DataSourcesView;